import { JSX, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FaArrowRight } from 'react-icons/fa6';
import { useParams } from 'react-router-dom';
import { HabitBundle, HabitBundles, HabitConfig, PredefinedHabits } from '../../config/habit.config';
import { HabitFrequencyType } from '../../dto/habit.enum';
import { useLayout } from '../../hooks/layout.hook';
import { useNavigation } from '../../hooks/navigation.hook';

export interface FormData {
  target?: string;
  frequency: HabitFrequencyType;
}

export default function HabitAddScreen(): JSX.Element {
  const { t } = useTranslation();
  const { navigate } = useNavigation();
  const { category: selectedCategory } = useParams();

  const category = selectedCategory != null ? PredefinedHabits[+selectedCategory] : undefined;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [selectedCategory]);

  function selectCategory(category: number) {
    navigate(`/habits/add/${category}`);
  }

  function selectBundle(bundle: HabitBundle) {
    // copy and translate the bundle
    const newBundle = structuredClone(bundle);
    newBundle.habits.forEach((h) => {
      h.title = t(h.title);
      h.notes = h.notes && t(h.notes);
    });

    navigate(`/habits/bundle`, { replace: false, state: newBundle });
  }

  function addHabit(habit: HabitConfig) {
    navigate('/habits/new', { replace: false, state: { ...habit, category: category?.name } });
  }

  useLayout(
    () => ({
      activeMenu: 'habits',
      title: category ? t(category.name) : t('Add Habit'),
      titleSize: category ? 'text-xl' : 'text-2xl',
      backButton: true,
    }),
    [t, category],
  );

  return (
    <>
      {category ? (
        <ul className="list bg-base-100 rounded-box shadow-md">
          {category.habits.map((h) => (
            <li
              className="list-row flex items-center cursor-pointer transition duration-200 hover:bg-base-300 active:bg-base-200"
              key={h.title}
              onClick={() => addHabit(h)}
            >
              <div className="flex-grow-1 text-left">
                <div className="font-bold text-lg">{t(h.title)}</div>
                {h.description && (
                  <div className="font-normal text-base-content/70 whitespace-pre-line hyphens-auto">
                    {t(h.description)}
                  </div>
                )}
              </div>
              <div>
                <FaArrowRight />
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <>
          <ul className="list bg-primary/30 rounded-box shadow-md mb-3">
            {HabitBundles.filter(
              (b) =>
                (!b.availableFrom || new Date() > b.availableFrom) && (!b.availableTo || new Date() < b.availableTo),
            ).map((c) => (
              <li
                key={c.name}
                className="list-row flex items-center cursor-pointer transition duration-200 hover:bg-primary/40 active:bg-primary/50"
                onClick={() => selectBundle(c)}
              >
                <div className="flex-grow-1 font-bold text-lg text-left">{t(c.name)}</div>
                <div>
                  <FaArrowRight />
                </div>
              </li>
            ))}
          </ul>

          <ul className="list bg-base-100 rounded-box shadow-md">
            {PredefinedHabits.map((c, i) => (
              <li
                key={c.name}
                className="list-row flex items-center cursor-pointer transition duration-200 hover:bg-base-300 active:bg-base-200"
                onClick={() => selectCategory(i)}
              >
                <div className="flex-grow-1 font-bold text-lg text-left">{t(c.name)}</div>
                <div>
                  <FaArrowRight />
                </div>
              </li>
            ))}
          </ul>
        </>
      )}
    </>
  );
}
